<template>
  <div class="dev-dashboard">
    <section class="dev-dashboard__header">
      <div>
        <p class="dev-dashboard__eyebrow">Development Board</p>
        <h1>开发进度看板</h1>
      </div>
      <div class="dev-dashboard__progress-mini">
        <div
          class="progress-pie"
          :style="{ '--progress': accomplishedPercent + '%' }"
        >
          <strong>{{ accomplishedPercent }}%</strong>
        </div>
        <div>
          <span>开发进度</span>
        </div>
      </div>
    </section>

    <section class="progress-card">
      <div class="progress-card__head">
        <div>
          <p>整体进度</p>
          <strong>{{ accomplishedPercent }}%</strong>
        </div>
        <span>{{ accomplishedCount }} / {{ effectiveTotal }} 已完成</span>
      </div>

      <div class="progress-card__track">
        <i
          v-for="item in summaryList"
          :key="item.value"
          :class="getStatusClass(item.value)"
          :style="{ width: item.percent + '%' }"
        ></i>
      </div>

      <div class="progress-card__legend">
        <button
          v-for="item in summaryList"
          :key="item.value"
          type="button"
          :class="{ 'is-active': activeStatusFilter === item.value }"
          @click="toggleStatusFilter(item.value)"
        >
          <span
            class="status-dot"
            :class="getStatusClass(item.value)"
          ></span>
          <span>{{ item.label }}</span>
          <strong>{{ item.count }}</strong>
          <em>{{ item.percent }}%</em>
        </button>
      </div>
    </section>

    <section class="route-panel">
      <div class="route-panel__head">
        <h2>路由列表</h2>
        <div class="right-wrapper">
          <div class="route-filter">
            <input
              v-model.trim="routeFilterKeyword"
              type="text"
              placeholder="过滤"
            >
            <button
              v-if="routeFilterKeyword"
              type="button"
              @click="clearRouteFilter"
            >
              ×
            </button>
          </div>
          <span>{{ filteredRouteList.length }} 项</span>
        </div>
      </div>

      <div class="route-list">
        <article
          v-for="route in filteredRouteList"
          :key="route.key"
          class="route-card"
        >
          <div class="route-card__main">
            <div class="route-card__title-row">
              <h3>{{ route.name || route.path }}</h3>
              <span
                class="status-badge"
                :class="getStatusClass(route.devStatus)"
              >
                {{ route.devStatus }}
              </span>
            </div>
            <p>{{ route.fullPath }}</p>
          </div>

          <div class="route-card__actions">
            <div class="route-card__suffix-slot">
              <input
                v-show="expandedRouteKey === route.key"
                :ref="`suffixInput-${route.key}`"
                :value="routeSuffixMap[route.key] || ''"
                type="text"
                placeholder="?id=1"
                @input="updateRouteSuffix(route.key, $event.target.value)"
              >
              <button
                v-if="expandedRouteKey === route.key && routeSuffixMap[route.key]"
                type="button"
                class="route-card__clear-btn"
                @click="clearRouteSuffix(route.key)"
              >
                ×
              </button>
            </div>
            <button
              type="button"
              class="route-card__secondary-btn"
              @click="toggleRouteSuffix(route.key)"
            >
              后缀
            </button>
            <button
              type="button"
              class="route-card__primary-btn"
              @click="openRoute(route)"
            >
              打开
            </button>
          </div>
        </article>
      </div>
    </section>

    <transition name="dev-back-top-fade">
      <button
        v-show="showBackTop"
        type="button"
        class="dev-dashboard__back-top"
        title="回到顶部"
        aria-label="回到顶部"
        @click="scrollToTop"
      >
        <i class="el-icon-arrow-up"></i>
      </button>
    </transition>
  </div>
</template>

<script>
const ROUTE_SUFFIX_STORAGE_KEY = 'youxi_dev_route_suffix_map'
const DEV_ROUTE_NAME = 'Dev'
const DEV_ROUTE_PATH = '/dev'
const STATUS_OPTIONS = [
  {
    label: '待换皮',
    value: '待换皮'
  },
  {
    label: '换皮完毕',
    value: '换皮完毕'
  },
  {
    label: '无需调整',
    value: '无需调整'
  }
]

export default {
  data() {
    return {
      activeStatusFilter: '',
      expandedRouteKey: '',
      routeFilterKeyword: '',
      routeSuffixMap: {},
      showBackTop: false,
      statusOptions: STATUS_OPTIONS
    }
  },
  computed: {
    routeList() {
      return this.flattenRoutes(this.$router.options.routes)
        .filter(route => route.name !== DEV_ROUTE_NAME && route.fullPath !== DEV_ROUTE_PATH)
        .map(route => ({
          ...route,
          devStatus: route.meta && route.meta.devStatus ? route.meta.devStatus : '未设置'
        }))
    },
    filteredRouteList() {
      const keyword = this.routeFilterKeyword.toLowerCase()

      if (!keyword && !this.activeStatusFilter) {
        return this.routeList
      }

      return this.routeList.filter(route => {
        const name = route.name ? String(route.name).toLowerCase() : ''
        const path = route.path ? String(route.path).toLowerCase() : ''
        const fullPath = route.fullPath ? String(route.fullPath).toLowerCase() : ''
        const matchesKeyword = !keyword ||
          name.indexOf(keyword) > -1 ||
          path.indexOf(keyword) > -1 ||
          fullPath.indexOf(keyword) > -1
        const matchesStatus = !this.activeStatusFilter ||
          route.devStatus === this.activeStatusFilter

        return matchesKeyword && matchesStatus
      })
    },
    summaryList() {
      const statusMap = this.routeList.reduce((map, route) => {
        map[route.devStatus] = (map[route.devStatus] || 0) + 1
        return map
      }, {})

      return this.statusOptions.map(item => {
        const count = statusMap[item.value] || 0
        const percent = this.routeList.length
          ? Math.round((count / this.routeList.length) * 100)
          : 0

        return {
          ...item,
          count,
          percent
        }
      })
    },
    accomplishedCount() {
      const item = this.summaryList.find(item => item.value === '换皮完毕')
      return item ? item.count : 0
    },
    noNeedCount() {
      const item = this.summaryList.find(item => item.value === '无需调整')
      return item ? item.count : 0
    },
    effectiveTotal() {
      return Math.max(this.routeList.length - this.noNeedCount, 0)
    },
    accomplishedPercent() {
      if (!this.effectiveTotal) {
        return 100
      }

      return Math.round((this.accomplishedCount / this.effectiveTotal) * 100)
    }
  },
  created() {
    this.routeSuffixMap = this.getSavedRouteSuffixMap()
  },
  mounted() {
    this.handleBackTopVisible()
    window.addEventListener('scroll', this.handleBackTopVisible, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleBackTopVisible)
  },
  methods: {
    flattenRoutes(routes, parentPath = '') {
      return routes.reduce((list, route) => {
        const fullPath = this.resolveRoutePath(parentPath, route.path)
        const routeItem = {
          key: `${route.name || 'anonymous'}::${fullPath}`,
          name: route.name,
          path: route.path,
          fullPath,
          meta: route.meta || {}
        }

        list.push(routeItem)

        if (route.children && route.children.length) {
          list.push(...this.flattenRoutes(route.children, fullPath))
        }

        return list
      }, [])
    },
    resolveRoutePath(parentPath, path) {
      if (!path) {
        return parentPath || '/'
      }

      if (path === '*') {
        return path
      }

      if (path.indexOf('/') === 0) {
        return path
      }

      const normalizedParent = parentPath === '/' ? '' : parentPath
      return `${normalizedParent}/${path}`.replace(/\/+/g, '/')
    },
    getSavedRouteSuffixMap() {
      try {
        return JSON.parse(localStorage.getItem(ROUTE_SUFFIX_STORAGE_KEY)) || {}
      } catch (error) {
        return {}
      }
    },
    saveRouteSuffix(routeKey) {
      localStorage.setItem(
        ROUTE_SUFFIX_STORAGE_KEY,
        JSON.stringify({
          ...this.routeSuffixMap,
          [routeKey]: this.routeSuffixMap[routeKey] || ''
        })
      )
    },
    getStatusClass(status) {
      const statusClassMap = {
        待换皮: 'is-untouched',
        换皮完毕: 'is-accomplished',
        无需调整: 'is-no-need'
      }

      return statusClassMap[status] || 'is-unknown'
    },
    clearRouteFilter() {
      this.routeFilterKeyword = ''
    },
    toggleStatusFilter(status) {
      this.activeStatusFilter = this.activeStatusFilter === status ? '' : status
    },
    updateRouteSuffix(routeKey, value) {
      this.$set(this.routeSuffixMap, routeKey, value.trim())
      this.saveRouteSuffix(routeKey)
    },
    toggleRouteSuffix(routeKey) {
      const isOpening = this.expandedRouteKey !== routeKey
      this.expandedRouteKey = isOpening ? routeKey : ''

      if (isOpening) {
        this.$nextTick(() => {
          const input = this.$refs[`suffixInput-${routeKey}`]
          const target = Array.isArray(input) ? input[0] : input

          if (target) {
            target.focus()
          }
        })
      }
    },
    clearRouteSuffix(routeKey) {
      this.$set(this.routeSuffixMap, routeKey, '')
      this.saveRouteSuffix(routeKey)

      this.$nextTick(() => {
        const input = this.$refs[`suffixInput-${routeKey}`]
        const target = Array.isArray(input) ? input[0] : input

        if (target) {
          target.focus()
        }
      })
    },
    openRoute(route) {
      const suffix = this.routeSuffixMap[route.key] || ''
      const target = this.getTargetPath(route.fullPath, suffix)
      const routeData = this.$router.resolve(target)

      window.open(routeData.href, '_blank')
    },
    getTargetPath(path, suffix) {
      if (!suffix) {
        return path
      }

      if (suffix.indexOf('?') === 0 || suffix.indexOf('#') === 0) {
        return `${path}${suffix}`
      }

      if (path === '/') {
        return suffix.indexOf('/') === 0 ? suffix : `/${suffix}`
      }

      return `${path.replace(/\/$/, '')}/${suffix.replace(/^\//, '')}`
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    handleBackTopVisible() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
      this.showBackTop = scrollTop > 240
    }
  }
}
</script>

<style lang="less" scoped>
.dev-dashboard {
  align-self: stretch;
  width: 100%;
  min-height: calc(100vh + 1px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  background: var(--bg-page);
  color: var(--text-main);
  scrollbar-gutter: stable;

  > section {
    width: 100%;
    max-width: 600px;
    box-sizing: border-box;
  }
}

.dev-dashboard__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;

  h1 {
    margin: 4px 0 0;
    font-size: 28px;
    font-weight: 700;
    line-height: 1.3;
  }
}

.dev-dashboard__eyebrow {
  margin: 0;
  color: var(--primary);
  font-size: 13px;
  font-weight: 600;
}

.dev-dashboard__progress-mini {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 184px;
  min-height: 78px;
  padding: 12px 14px;
  border: 1px solid var(--border-soft);
  border-radius: 4px;
  background: var(--bg-card);
  box-shadow: var(--shadow-card);

  span {
    display: block;
    color: var(--text-secondary);
    font-size: 13px;
  }

  em {
    display: block;
    margin-top: 4px;
    color: var(--text-main);
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
  }
}

.progress-pie {
  display: grid;
  place-items: center;
  flex: 0 0 54px;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: conic-gradient(var(--success) var(--progress), var(--bg-card-soft) 0);
  position: relative;

  &::after {
    position: absolute;
    inset: 7px;
    border-radius: 50%;
    background: var(--bg-card);
    content: '';
  }

  strong {
    position: relative;
    z-index: 1;
    font-size: 13px;
    line-height: 1;
  }
}

.progress-card,
.route-panel,
.route-card {
  border: 1px solid var(--border-soft);
  border-radius: 4px;
  background: var(--bg-card);
  box-shadow: var(--shadow-card);
}

.progress-card {
  margin-bottom: 16px;
  padding: 20px;
}

.progress-card__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;

  p {
    margin: 0;
    color: var(--text-secondary);
    font-size: 13px;
  }

  strong {
    display: block;
    margin-top: 4px;
    font-size: 36px;
    line-height: 1;
  }

  span {
    color: var(--text-secondary);
    font-size: 13px;
  }
}

.progress-card__track {
  display: flex;
  overflow: hidden;
  height: 22px;
  border: 1px solid var(--border-soft);
  border-radius: 4px;
  background: var(--bg-card-soft);

  i {
    display: block;
    height: 100%;
  }
}

.progress-card__legend {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;

  button {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 10px 12px;
    border: 1px solid var(--border-soft);
    border-radius: 4px;
    background: var(--bg-section);
    color: var(--text-secondary);
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    text-align: left;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;
  }

  button:hover {
    border-color: rgba(26, 29, 36, 0.32);
    background: var(--bg-tag);
    box-shadow: 0 6px 16px rgba(26, 29, 36, 0.08);
  }

  button.is-active {
    border-color: var(--primary);
    background: var(--primary-soft);
    color: var(--primary);
    box-shadow: inset 0 0 0 1px rgba(255, 90, 31, 0.24), 0 8px 18px rgba(255, 90, 31, 0.12);
  }

  strong {
    margin-left: auto;
    color: var(--text-main);
    font-size: 16px;
  }

  button.is-active strong,
  button.is-active em {
    color: var(--primary);
  }

  em {
    color: var(--text-muted);
    font-style: normal;
  }
}

.route-panel {
  padding: 14px;
}

.route-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;

  h2 {
    margin: 0;
    font-size: 18px;
  }

  .right-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
    flex: 1;

    span {
      color: var(--text-secondary);
      font-size: 13px;
      min-width: 40px;
      text-align: right;
    }
  }

}

.route-filter {
  position: relative;

  input {
    height: 32px;
    padding: 0 32px 0 10px;
    border: 1px solid var(--border-soft);
    border-radius: 4px;
    color: var(--text-main);
    font-size: 13px;
    outline: none;
  }

  input:focus {
    border-color: var(--primary);
    box-shadow: none;
  }

  input::placeholder {
    color: var(--text-muted);
  }

  button {
    position: absolute;
    top: 50%;
    right: 6px;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: var(--bg-card-soft);
    color: var(--text-secondary);
    cursor: pointer;
    font-size: 16px;
    line-height: 18px;
    transform: translateY(-50%);
  }
}

.route-list {
  display: grid;
  gap: 8px;
}

.route-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 68px;
  padding: 10px 12px;
  box-shadow: none;
}

.route-card__main {
  flex-shrink: 0;
  min-width: 0;

  p {
    overflow: hidden;
    margin: 5px 0 0;
    color: var(--text-secondary);
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.route-card__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  h3 {
    overflow: hidden;
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.4;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.route-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;

  input,
  button {
    height: 32px;
    border-radius: 4px;
    font-size: 13px;
  }

  input {
    flex: 1;
    min-width: 0;
    padding: 0 10px;
    border: 1px solid var(--border-soft);
    color: var(--text-main);
    outline: none;
  }

  input:focus {
    border-color: var(--primary);
    box-shadow: none;
  }

  input::placeholder {
    color: var(--text-muted);
  }

  button {
    padding: 0 12px;
    cursor: pointer;
    white-space: nowrap;
  }
}

.route-card__suffix-slot {
  position: relative;

  input {
    padding-right: 32px;
  }
}

.route-card__clear-btn {
  position: absolute;
  top: 50%;
  right: 6px;
  width: 20px;
  min-width: 20px;
  height: 20px !important;
  padding: 0 !important;
  border: 0;
  border-radius: 50% !important;
  background: var(--bg-card-soft);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 16px !important;
  line-height: 18px;
  transform: translateY(-50%);
}

.route-card__secondary-btn {
  border: 1px solid var(--border-soft);
  background: var(--bg-card);
  color: var(--text-secondary);
}

.route-card__primary-btn {
  border: 0;
  background: var(--primary);
  color: #fff;
}

.dev-dashboard__back-top {
  position: fixed;
  right: 32px;
  bottom: 32px;
  z-index: 20;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 1px solid rgba(26, 29, 36, 0.36);
  border-radius: 50%;
  background: var(--bg-dark);
  color: #fff;
  box-shadow: 0 12px 28px rgba(26, 29, 36, 0.26);
  cursor: pointer;
  font-size: 18px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: rgba(26, 29, 36, 0.52);
    filter: brightness(1.04);
    box-shadow: 0 14px 32px rgba(26, 29, 36, 0.32);
  }
}

.dev-back-top-fade-enter-active,
.dev-back-top-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dev-back-top-fade-enter,
.dev-back-top-fade-leave-to {
  opacity: 0;
  transform: scale(0.88);
}

.status-badge {
  flex: 0 0 auto;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
}

.status-dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.is-untouched {
  background: var(--warning);
  color: var(--warning);
}

.status-badge.is-untouched {
  background: var(--warning-soft);
}

.is-accomplished {
  background: var(--success);
  color: var(--success);
}

.status-badge.is-accomplished {
  background: var(--success-soft);
}

.is-no-need {
  background: var(--bg-dark);
  color: var(--bg-dark);
}

.status-badge.is-no-need {
  background: var(--bg-tag);
}

.is-unknown {
  background: var(--text-muted);
  color: var(--text-muted);
}

.status-badge.is-unknown {
  background: var(--bg-card-soft);
}

@media (max-width: 900px) {
  .dev-dashboard {
    padding: 20px;
  }

  .dev-dashboard__header {
    align-items: stretch;
    flex-direction: column;
  }

  .dev-dashboard__progress-mini {
    flex-basis: auto;
  }

  .progress-card__legend {
    grid-template-columns: 1fr;
  }

  .route-card__actions {
    justify-content: start;
  }
}

@media (max-width: 520px) {
  .dev-dashboard {
    padding: 14px;
  }

  .dev-dashboard__back-top {
    right: 18px;
    bottom: 18px;
    width: 40px;
    height: 40px;
  }

  .route-panel__head {
    align-items: stretch;
    flex-direction: column;
  }

  .route-filter {
    width: 100%;
    margin-left: 0;
  }

  .route-card__actions {
    grid-template-columns: 1fr 1fr;

    input {
      grid-column: 1 / -1;
      width: 100%;
    }
  }
}
</style>
